import React from 'react';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useModal } from '../ModalContext';
import ProjectModal from '../../../views/jobs/ProjectModal';

/*
 * GenerateHeaderTitle.tsx
 * Generate the header title for detail modals based on the api 
 * object of the modal.
 */

interface HeaderTitleProps {
    apiObject?: string;
    data?: any;
}

const GenerateHeaderTitle: React.FC<HeaderTitleProps> = ({ apiObject, data }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();

    if (!data || !apiObject) {
        return null;
    }

    // Handle open the project modal
    const handleOpenProjectModal = (projectId: number | null) => {
        if (!projectId) return;
        initializeModal(
            React.cloneElement(<ProjectModal />), { 
                itemId: projectId,
                modalSize: 'large-without-sidetab'
            } 
        );
    }

    // Render deleted badge


    let headerTitle = null;

    // Generate the header for jobs
    if (apiObject === 'job') {
        // Header for a single job
		if (data['project'] === null) {
			headerTitle = <h2>{t('job.general.job_header', { number: t(data['number']) })}</h2>;

        // Header for a sub job inside a project
        } else {
            headerTitle = (
                <h2>
                    <span
                        style={{ textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={() => handleOpenProjectModal(data['project']?.id)}
                    >
                        {t('job.general.project_header', { number: t(data['project'].number) })}
                    </span>
                    {" "}
                    <FontAwesomeIcon 
                        icon={faChevronRight} 
                        style={{ fontSize: '18px', color: '#9b9b9b' }}
                    />
                    {" "}
                    {t('job.general.job_header', { number: t(data['number']) })}
                </h2>
            );
        }

    // Generate the header for a project
    } else if (apiObject === 'project') {
        headerTitle = (
            <div className='title-with-badge'>
                <h2>{t('job.general.project_header', { number: t(data['number']) })}</h2>
                {data['closed'] && (
                    <span className="header-title-badge closed">
                        {t('project.general.project_closed_label')}
                    </span>
                )}
            </div>
        );

    // Generate the header for a proposal
	} else if (apiObject === 'proposal') {
        // Header for draft proposals
        if (data['status'] === 'draft') {
            headerTitle = <h2>{t('proposal.general.proposal_draft_header', { number: t(data['number']) })}</h2>;

        // Header for sent proposals
        } else {
            headerTitle = <h2>{t('proposal.general.proposal_header', { number: t(data['number']) })}</h2>;
        }    

    // Generate the header for an order
    } else if (apiObject === 'order') {
        headerTitle = <h2>{t('order.general.order_header', { number: t(data['number']) })}</h2>;

    // Generate the header for an purchase
	} else if (apiObject === 'purchase') {
        // Header for draft proposals
        if (data['status'] === 'draft') {
            headerTitle = <h2>{t('purchase.general.purchase_draft_header', { number: t(data['number']) })}</h2>;

        // Header for sent proposals
        } else {
            headerTitle = <h2>{t('purchase.general.purchase_header', { number: t(data['number']) })}</h2>;
        }  

    // Generate the header for an invoice
	} else if (apiObject === 'invoice') {
        // Determine the translation prefix, credit invoices show their own title
        const invoicePrefix = data['is_credit_invoice'] ? 'credit_invoice' : 'invoice';

        // Pro forma invoices show the invoice specification title without their number
        if (data['invoice_type'] === 'proforma' && !data['is_credit_invoice']) {
            headerTitle = <h2>{t('invoice.general.proforma_header')}</h2>;

        // Header for draft invoices
        } else if (data['status'] === 'draft') {
            headerTitle = <h2>{t(`invoice.general.${invoicePrefix}_draft_header`, { number: t(data['number']) })}</h2>;
        
        // Header for sent invoices
        } else {
            headerTitle = <h2>{t(`invoice.general.${invoicePrefix}_header`, { number: t(data['number']) })}</h2>;
        }

    // Generate the header for a periodic invoice
    } else if (apiObject === 'invoice_template') {
        headerTitle = <h2>{data['number'] ? t('invoice.template.header_with_number', { number: data['number'] }) : t('invoice.template.header')}</h2>;

    // Generate the header for a release
    } else if (apiObject === 'release') {
        headerTitle = <h2>{t('release.general.edit_release_header', { version: t(data['version'] )})}</h2>

    // Render just the header title
    } else {
        headerTitle = <h2>{headerTitle}</h2>
    }

    // If the item is deleted, add the deleted status badge
    if (data['deleted']) {
        return (
            <div className='title-with-badge'>
                {headerTitle}
                <span className="header-title-badge deleted">
                    {t('general.deleted')}
                </span>
            </div>
        );
    }

    return headerTitle;
};

export default GenerateHeaderTitle;